import { Box, Button, Flex, RadioGroup, Text, VStack } from '@chakra-ui/react';
import { ReactElement, useEffect, useState } from 'react';
import { ModalAdminInviteVariantProps, SharedModalProps } from 'types/Modal';
import { EmailChipInput } from 'components/common/ChipInput/EmailChipInput';
import { useNovaToast } from 'hooks/utils/useNovaToast';
import { toastContent } from 'config/toast';
import { ToastVariant } from 'types/Toast';
import { Filters } from 'components/common/Table/Filters';
import { AdminInviteResponse, useInviteUsers } from 'hooks/api/backend/managament/getB2BUsers';
import { formattedDateWithMonthName } from 'utils/formattedDate';
import { VoucherOption } from 'components/common/Table/FilterOptions';
import { ManagementStatusType } from 'types/StatusTypes';
import { SchoolUserRole } from '@novaheal/types';
import { CustomModal } from '../CustomModal';
import { CustomModalContent } from '../CustomModalContent';

type Props = SharedModalProps & ModalAdminInviteVariantProps;

export const AdminInviteModal = ({
  handleClose,
  isShown,
  selectedVoucherId,
  allVouchers,
}: Props): ReactElement => {
  const { mutateAsync: inviteUsers, isPending } = useInviteUsers();
  const toast = useNovaToast();

  const [emailValues, setEmailValues] = useState<string[]>([]);
  const [voucherInput, setVoucherInput] = useState('');
  const [selectedRole, setSelectedRole] = useState<SchoolUserRole>(SchoolUserRole.STUDENT);

  const handleSubmit = async (): Promise<void> => {
    if (!voucherInput) {
      toast.show(toastContent.management[ManagementStatusType.SELECT_VOUCHER]);
      return;
    }

    if (emailValues.length === 0) {
      toast.show({
        title: 'Bitte gib mindestens eine E-Mail-Adresse ein',
        variant: ToastVariant.ERROR,
      });
      return;
    }

    const inviteStatus = await inviteUsers({
      voucherId: voucherInput,
      emails: emailValues,
      ...(selectedRole === SchoolUserRole.TEACHER && { role: SchoolUserRole.TEACHER }),
      ...(selectedRole === SchoolUserRole.GUIDE && { role: SchoolUserRole.GUIDE }),
    });

    switch (inviteStatus) {
      case AdminInviteResponse.INVITE_SUCCESS:
        toast.show(
          emailValues.length > 1
            ? toastContent.management.inviteSuccessPlural
            : toastContent.management.inviteSuccess
        );
        handleClose();
        setEmailValues([]);
        setSelectedRole(SchoolUserRole.STUDENT);
        break;
      case AdminInviteResponse.INVITE_ERROR:
        toast.show(
          emailValues.length > 1
            ? toastContent.management.inviteErrorPlural
            : toastContent.management.inviteError
        );
        break;

      default:
        toast.show({ ...toastContent.error.default, variant: ToastVariant.ERROR });
        break;
    }
  };

  const handleChangeVoucher = (value: string): void => {
    setVoucherInput(value);
  };

  useEffect(() => {
    if (selectedVoucherId && allVouchers) {
      const selectedVoucher = allVouchers.find((voucher) => voucher.id === selectedVoucherId);
      if (selectedVoucher && selectedVoucher.allowedUsage > selectedVoucher.timesUsed) {
        setVoucherInput(selectedVoucherId);
      } else {
        setVoucherInput('');
      }
    }
  }, [selectedVoucherId, allVouchers]);

  return (
    <CustomModal
      isOpen={isShown}
      onClose={handleClose}
      placement="center"
      size="md"
      hideHeader={false}
    >
      <CustomModalContent
        p={{ base: '0', md: '0 40px 30px' }}
        display="flex"
        flexDirection="column"
        overflowY="auto"
      >
        <Flex
          p={{ base: 'l', md: '0' }}
          textAlign={{ base: 'center', md: 'left' }}
          flexDir="column"
          gap="30px"
        >
          <VStack gap="l" align="stretch">
            <VStack gap="15px" align="stretch">
              <Text textStyle="h3.medium" color="text.primary">
                Zu Novaheal einladen
              </Text>
              <Text textStyle="body.m.regular" color="text.tertiary">
                Wähle die Lizenz aus, füge eine oder mehrere E-Mail-Adressen hinzu und lege die
                Rollen fest. Wir senden dann eine Einladung mit einem persönlichen Zugangscode an
                die Personen.
              </Text>
            </VStack>

            {allVouchers && allVouchers.length > 0 && (
              <Filters
                filters={[
                  [
                    {
                      id: 'voucher',
                      placeholder: 'Lizenz auswählen',
                      value: voucherInput,
                      element: VoucherOption,
                      placeholderColor: 'text.tertiary',
                      options: allVouchers.map(
                        ({ id, shortCode, validUntil, timesUsed, allowedUsage }) => ({
                          id: id as string,
                          label: shortCode,
                          date: formattedDateWithMonthName(validUntil),
                          allowedUsage,
                          timesUsed,
                          disabled: allowedUsage <= timesUsed,
                        })
                      ),
                      onChange: handleChangeVoucher,
                    },
                  ],
                ]}
              />
            )}

            <EmailChipInput
              placeholder="E-Mail-Adressen hinzufügen..."
              values={emailValues}
              onChange={setEmailValues}
              width="100%"
              minWidth="100%"
              maxHeight="150px"
            />
          </VStack>

          <VStack gap="15px" align="stretch">
            <VStack gap="8px" align="stretch">
              <Text textStyle="body.s.medium" color="text.primary">
                Rollenzuweisung:
              </Text>
              <Text textStyle="body.xs.regular" color="text.secondary">
                Wähle eine Rolle für alle hinzugefügten Personen:
              </Text>
            </VStack>

            <RadioGroup.Root
              value={selectedRole}
              onValueChange={(details) => setSelectedRole(details.value as SchoolUserRole)}
              colorPalette="green"
            >
              <VStack gap="15px" align="stretch">
                <VStack gap="xs" align="stretch">
                  <RadioGroup.Item value={SchoolUserRole.TEACHER} display="flex" gap="xs">
                    <RadioGroup.ItemHiddenInput />
                    <RadioGroup.ItemIndicator />
                    <RadioGroup.ItemText textStyle="body.s.medium" color="text.primary">
                      Lehrende
                    </RadioGroup.ItemText>
                  </RadioGroup.Item>
                  <Box pl="26px">
                    <Text textStyle="body.xs.regular" color="text.primary">
                      Erstellt und teilt Materialien, bereitet Arbeitsblätter vor, weist Aufgaben zu
                      und sieht den Lernfortschritt.
                    </Text>
                  </Box>
                </VStack>

                <VStack gap="xs" align="stretch">
                  <RadioGroup.Item value={SchoolUserRole.GUIDE} display="flex" gap="xs">
                    <RadioGroup.ItemHiddenInput />
                    <RadioGroup.ItemIndicator />
                    <RadioGroup.ItemText textStyle="body.s.medium" color="text.primary">
                      Praxisanleitende
                    </RadioGroup.ItemText>
                  </RadioGroup.Item>
                  <Box pl="26px">
                    <Text textStyle="body.xs.regular" color="text.primary">
                      Erstellt und teilt Materialien, bereitet Arbeitsblätter vor, weist Aufgaben zu
                      und sieht den Lernfortschritt.
                    </Text>
                  </Box>
                </VStack>

                <VStack gap="xs" align="stretch">
                  <RadioGroup.Item value={SchoolUserRole.STUDENT} display="flex" gap="xs">
                    <RadioGroup.ItemHiddenInput />
                    <RadioGroup.ItemIndicator />
                    <RadioGroup.ItemText textStyle="body.s.medium" color="text.primary">
                      Lernende
                    </RadioGroup.ItemText>
                  </RadioGroup.Item>
                  <Box pl="26px">
                    <Text textStyle="body.xs.regular" color="text.primary">
                      Lernt mit NOVAHEAL, bearbeitet Aufgaben und verfolgt den eigenen Fortschritt.
                    </Text>
                  </Box>
                </VStack>
              </VStack>
            </RadioGroup.Root>
          </VStack>
        </Flex>

        <Box mt="50px">
          <Flex gap="10px" width="100%" justifyContent="flex-end">
            <Button onClick={handleClose} variant="tertiary" fontWeight="regular">
              Abbrechen
            </Button>
            <Button onClick={handleSubmit} variant="primary" loading={isPending}>
              {emailValues.length > 1 ? 'Einladungen senden' : 'Einladung senden'}
            </Button>
          </Flex>
        </Box>
      </CustomModalContent>
    </CustomModal>
  );
};
